
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const usePurchaseRequestStatusUpdate = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, status }: { requestId: string; status: string }) => {
      console.log('Updating purchase request status:', { requestId, status });
      
      // Vérifier l'authentification
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      if (authError || !user) {
        console.error('Authentication error:', authError);
        throw new Error('Utilisateur non authentifié');
      }

      // Vérifier que la demande appartient au professionnel
      const { data: request, error: fetchError } = await supabase
        .from("product_purchase_requests")
        .select("id, professional_id, status, buyer_id")
        .eq("id", requestId)
        .single();

      if (fetchError) {
        console.error('Error fetching purchase request:', fetchError);
        throw new Error(`Erreur lors de la récupération de la demande: ${fetchError.message}`);
      }

      if (!request) {
        throw new Error('Demande d\'achat introuvable');
      }

      if (request.professional_id !== user.id) {
        throw new Error('Vous n\'avez pas l\'autorisation de modifier cette demande');
      }

      if (request.status !== 'pending') {
        throw new Error("Cette demande a déjà été mise à jour.");
      }

      // Valider le statut
      const validStatuses = ['pending', 'approved', 'rejected'];
      if (!validStatuses.includes(status)) {
        throw new Error(`Statut invalide: ${status}`);
      }

      let updateData: any = { 
        status,
        updated_at: new Date().toISOString()
      };
      
      if (status === "approved") {
        // Générer un numéro de reçu unique
        const receiptNumber = `REC-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        updateData.receipt_number = receiptNumber;
      }

      // Effectuer la mise à jour
      const { data, error: updateError } = await supabase
        .from("product_purchase_requests")
        .update(updateData)
        .eq("id", requestId)
        .eq("professional_id", user.id) // Double vérification pour la sécurité
        .select()
        .single();

      if (updateError) {
        console.error('Error updating purchase request:', updateError);
        throw new Error(`Erreur lors de la mise à jour: ${updateError.message}`);
      }

      // Si approuvé, envoyer un message avec le reçu
      if (status === "approved") {
        const { data: fullRequest } = await supabase
          .from("product_purchase_requests")
          .select(`
            *,
            product:professional_products!fk_product(*),
            buyer:profiles!product_purchase_requests_buyer_id_fkey(*)
          `)
          .eq("id", requestId)
          .single();

        if (fullRequest) {
          const messageContent = `
Votre demande d'achat a été approuvée !

Reçu N° : ${updateData.receipt_number}
Produit : ${fullRequest.product.title}
Prix : ${fullRequest.product.price}€
Quantité : ${fullRequest.quantity}
Total : ${fullRequest.product.price * fullRequest.quantity}€

Adresse de retrait : ${fullRequest.product.address}

Veuillez présenter ce reçu lors du retrait de votre produit.
          `;

          await supabase
            .from("messages")
            .insert({
              sender_id: user.id,
              receiver_id: fullRequest.buyer_id,
              content: messageContent,
            });
        }
      }

      console.log('Successfully updated purchase request:', data);
      return data;
    },
    onSuccess: (data, variables) => {
      console.log('Update successful, invalidating queries');
      
      queryClient.invalidateQueries({ queryKey: ["purchase-requests"] });
      
      const statusText = variables.status === 'approved' ? 'approuvée' : 
                        variables.status === 'rejected' ? 'refusée' : variables.status;
      
      toast({
        title: "Succès",
        description: `La demande d'achat a été ${statusText} avec succès`,
      });
    },
    onError: (error: Error) => {
      console.error("Detailed error updating purchase request status:", error);
      
      toast({
        title: "Information",
        description: error.message || "Impossible de mettre à jour le statut de la demande",
        variant: "destructive",
      });
    },
  });
};
